/* Links tab — per-program intake links + white-label brand editor.
 * Visible to brokers, underwriters, and jr_underwriters.
 *
 * Every program the user is authorized for shows a card with:
 *   • Copy Link button (permanent per-user-per-program token)
 *   • Send Invite (Resend email) — sends the same link with an optional message
 *   • App counter — how many apps have been created via that link
 * The brand editor at the top lets the user upload a logo, edit the display
 * company name, and pick an accent color. All of those apply to the branded
 * landing page a borrower sees when they click the shared link. */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Link as LinkIcon, Copy, Check, Mail, RefreshCw, Loader2, Palette,
  Upload, ImagePlus, Trash2, X, Send, Sparkles, ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_ACCENT_TAGS = {
  sba_loan:                 { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: '🇺🇸' },
  term_loan_10yr:           { chip: 'bg-blue-50 text-blue-700 border-blue-200',           icon: '🏛️' },
  term_loan_3_5yr:          { chip: 'bg-sky-50 text-sky-700 border-sky-200',              icon: '📈' },
  term_loc_hybrid:          { chip: 'bg-violet-50 text-violet-700 border-violet-200',     icon: '🔀' },
  line_of_credit:           { chip: 'bg-cyan-50 text-cyan-700 border-cyan-200',           icon: '💳' },
  equipment_financing:      { chip: 'bg-amber-50 text-amber-700 border-amber-200',        icon: '🚜' },
  merchant_processing:      { chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',  icon: '🧾' },
  jumbo_term_loan:          { chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',     icon: '💼' },
  transportation_factoring: { chip: 'bg-orange-50 text-orange-700 border-orange-200',     icon: '🚚' },
  real_estate_financing:    { chip: 'bg-teal-50 text-teal-700 border-teal-200',           icon: '🏢' },
  debt_consulting:          { chip: 'bg-rose-50 text-rose-700 border-rose-200',           icon: '⚖️' },
  foreclosure_bailout:      { chip: 'bg-red-50 text-red-700 border-red-200',              icon: '🛟' },
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const useIntakeData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${BACKEND_URL}/api/intake-links`, { headers: authHeaders() });
      setData(resp.data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load intake links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, error, reload: load };
};

// Rewrite the backend-emitted link origin with the current window origin so
// brokers copy a URL they can actually share externally, even when the
// backend falls back to an internal cluster hostname.
const rewriteOrigin = (link) => {
  if (!link || typeof window === 'undefined') return link;
  try {
    const u = new URL(link);
    u.protocol = window.location.protocol;
    u.host = window.location.host;
    return u.toString();
  } catch {
    return link;
  }
};

/* ---------- Brand editor: logo, company name, accent color ---------- */
const BrandEditor = ({ brand, userId, onSaved }) => {
  const [name, setName] = useState(brand?.company_name || '');
  const [color, setColor] = useState(brand?.primary_color || '#0f172a');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoCacheBust, setLogoCacheBust] = useState(Date.now());
  const [hasLogo, setHasLogo] = useState(!!brand?.has_logo);

  useEffect(() => {
    setName(brand?.company_name || '');
    setColor(brand?.primary_color || '#0f172a');
    setHasLogo(!!brand?.has_logo);
  }, [brand]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/intake-brand`, {
        company_name: name,
        primary_color: color,
      }, { headers: authHeaders() });
      if (onSaved) onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save brand');
    } finally {
      setSaving(false);
    }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('Max logo size is 10 MB'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BACKEND_URL}/api/profile/upload/company_logo`, form, {
        headers: { ...authHeaders() },
      });
      setHasLogo(true);
      setLogoCacheBust(Date.now());
      if (onSaved) onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove your uploaded logo?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/profile/file/company_logo`, { headers: authHeaders() });
      setHasLogo(false);
      if (onSaved) onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to remove logo');
    }
  };

  const logoSrc = hasLogo && userId
    ? `${BACKEND_URL}/api/intake-brand/logo/${userId}?t=${logoCacheBust}`
    : null;

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm mb-6" data-testid="intake-brand-editor">
      {/* Live preview header — mimics what borrowers see on the landing page */}
      <div style={{ backgroundColor: color }} className="px-6 py-6 flex items-center gap-4 transition-colors">
        <div className="h-16 w-16 rounded-xl bg-white/95 flex items-center justify-center overflow-hidden shrink-0 shadow">
          {logoSrc ? (
            <img src={logoSrc} alt="logo" className="max-h-14 max-w-14 object-contain" />
          ) : (
            <ImagePlus className="h-7 w-7 text-slate-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">Live Preview</p>
          <h3 className="text-xl font-bold text-white truncate" data-testid="intake-brand-preview-name">
            {name || 'Your Company Name'}
          </h3>
          <p className="text-xs text-white/80 mt-0.5">This is what borrowers see when they open your link</p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          {/* Logo upload */}
          <div>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Logo</Label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer" data-testid="intake-logo-upload-label">
                <input type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid="intake-logo-file-input" />
                <span className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {hasLogo ? 'Replace' : 'Upload'} Logo
                </span>
              </label>
              {hasLogo && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={removeLogo}
                  className="h-8 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  data-testid="intake-logo-remove-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Remove
                </Button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">PNG, JPG, or SVG · max 10 MB · shown at ~64×64px</p>
          </div>

          {/* Company name */}
          <div>
            <Label htmlFor="brand-name" className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Display Name</Label>
            <Input
              id="brand-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Capital Group"
              maxLength={120}
              className="h-9"
              data-testid="intake-brand-name-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5" /> Accent Color
          </Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-14 rounded-md cursor-pointer border border-slate-200"
              data-testid="intake-brand-color-input"
            />
            <div className="flex-1 flex flex-wrap gap-1.5">
              {['#0f172a', '#1e40af', '#0891b2', '#059669', '#7c3aed', '#c026d3', '#dc2626', '#ea580c'].map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="h-7 w-7 rounded-md ring-2 ring-transparent hover:ring-slate-300 transition"
                  style={{ backgroundColor: c, ringColor: color === c ? c : undefined }}
                  aria-label={`Pick ${c}`}
                  data-testid={`intake-color-swatch-${c.slice(1)}`}
                />
              ))}
              <div className="flex-1" />
              <Button
                size="sm"
                onClick={save}
                disabled={saving}
                className="ml-auto"
                data-testid="intake-brand-save-btn"
                style={{ backgroundColor: color }}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                Save Brand
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Send Invite modal ---------- */
const InviteModal = ({ open, onClose, link, accentColor, brandName }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) { setEmail(''); setMessage(''); setSent(false); }
  }, [open]);

  if (!open) return null;

  const send = async () => {
    if (!email || !/@/.test(email)) { alert('Enter a valid email'); return; }
    if (!link?.product_type) return;
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/intake-links/${link.product_type}/invite`,
        { email, message },
        { headers: authHeaders() },
      );
      setSent(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="intake-invite-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> Send Invitation
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Emailing <span className="font-semibold text-slate-700">{link?.label}</span> intake link — the borrower can click and start immediately.
          </p>
        </DialogHeader>

        {sent ? (
          <div className="py-8 text-center" data-testid="intake-invite-sent">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Invitation sent</p>
            <p className="text-xs text-slate-500 mt-1">{email}</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <Label htmlFor="invite-email" className="text-xs font-semibold">Borrower / Referral Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="borrower@example.com"
                  className="h-9 mt-1.5"
                  data-testid="intake-invite-email-input"
                />
              </div>
              <div>
                <Label htmlFor="invite-message" className="text-xs font-semibold">Optional Personal Note</Label>
                <Textarea
                  id="invite-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Hi — as we discussed, here's the ${link?.label} application link from ${brandName || 'us'}. Let me know if you have any questions.`}
                  className="mt-1.5 text-sm"
                  data-testid="intake-invite-message-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="intake-invite-cancel-btn">Cancel</Button>
              <Button
                onClick={send}
                disabled={sending}
                style={{ backgroundColor: accentColor }}
                data-testid="intake-invite-send-btn"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Per-program card ---------- */
const ProgramCard = ({ link, brand, onInvite, onRegenerate }) => {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const style = PROGRAM_ACCENT_TAGS[link.product_type] || PROGRAM_ACCENT_TAGS.term_loan_10yr;
  const displayLink = rewriteOrigin(link.link);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(displayLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { alert('Copy failed'); }
  };

  const regenerate = async () => {
    if (!window.confirm(`Rotate the ${link.label} link? The old URL will stop working immediately.`)) return;
    setRegenerating(true);
    try { await onRegenerate(link.product_type); } finally { setRegenerating(false); }
  };

  return (
    <div
      className="group rounded-xl border border-slate-200 bg-white hover:shadow-md hover:border-slate-300 transition-all overflow-hidden"
      data-testid={`intake-program-card-${link.product_type}`}
    >
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-lg leading-none" aria-hidden="true">{style.icon}</span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{link.label}</p>
            <p className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold mt-0.5" data-testid={`intake-program-count-${link.product_type}`}>
              {link.apps_count} {link.apps_count === 1 ? 'app' : 'apps'} received
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${style.chip}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
          Active
        </span>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-stretch gap-2">
          <Input
            readOnly
            value={displayLink}
            onFocus={(e) => e.target.select()}
            className="text-xs bg-slate-50 border-slate-300 flex-1 font-mono text-slate-900 selection:bg-cyan-200"
            data-testid={`intake-program-url-${link.product_type}`}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={copy}
            className="h-9 px-3 shrink-0 border-slate-300 text-slate-800 hover:text-slate-900"
            data-testid={`intake-program-copy-btn-${link.product_type}`}
          >
            {copied ? <><Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(displayLink, '_blank', 'noopener,noreferrer')}
            className="h-9 px-3 shrink-0 border-slate-300 text-slate-800 hover:text-slate-900"
            title="Open link in a new tab"
            data-testid={`intake-program-open-btn-${link.product_type}`}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
          </Button>
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={regenerate}
            disabled={regenerating}
            className="h-8 text-xs text-slate-500 hover:text-slate-700"
            data-testid={`intake-program-rotate-btn-${link.product_type}`}
          >
            {regenerating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Rotate
          </Button>
          <Button
            size="sm"
            onClick={() => onInvite(link)}
            className="h-8 text-xs"
            style={{ backgroundColor: brand?.primary_color || '#0f172a' }}
            data-testid={`intake-program-invite-btn-${link.product_type}`}
          >
            <Mail className="h-3.5 w-3.5 mr-1" /> Send Invite
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Main panel ---------- */
const LinksPanel = () => {
  const { data, loading, error, reload } = useIntakeData();
  const [inviteLink, setInviteLink] = useState(null);

  const regenerate = async (productType) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/intake-links/${productType}/regenerate`,
        {},
        { headers: authHeaders() },
      );
      reload();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to rotate link');
    }
  };

  const totalApps = useMemo(
    () => (data?.links || []).reduce((s, l) => s + (l.apps_count || 0), 0),
    [data],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading intake links…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-800 text-sm" data-testid="intake-error">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="links-panel">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-500" />
            Intake Links
          </h2>
          <p className="text-sm text-slate-700 mt-1 max-w-2xl">
            Share a personal link for any program. Borrowers and referral partners can start an application under your name — no login required.
          </p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 px-4 py-3 text-right shrink-0" data-testid="intake-total-apps-pill">
          <p className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Total from links</p>
          <p className="text-2xl font-bold text-slate-900 leading-tight">{totalApps}</p>
        </div>
      </div>

      <BrandEditor brand={data?.brand} userId={data?.user?.id} onSaved={reload} />

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="intake-programs-grid">
        {(data?.links || []).map(l => (
          <ProgramCard
            key={l.product_type}
            link={l}
            brand={data?.brand}
            onInvite={setInviteLink}
            onRegenerate={regenerate}
          />
        ))}
      </div>

      <InviteModal
        open={!!inviteLink}
        onClose={() => setInviteLink(null)}
        link={inviteLink}
        accentColor={data?.brand?.primary_color}
        brandName={data?.brand?.company_name}
      />
    </div>
  );
};

export default LinksPanel;
